import { QueryClient, queryOptions } from "@tanstack/react-query";
import type {
  RespostaAgenda,
  RespostaConexao,
  RespostaConfiguracoes,
  RespostaConversa,
  RespostaEmpresa,
  RespostaEquipe,
  RespostaEu,
  RespostaFunil,
  RespostaModelos,
  RespostaPacientes,
  RespostaRelatorio,
  RespostaSistema,
} from "@/servidor/tipos";
import type { ListaConversas } from "@/lib/telas/conversas";
import { api, ErroApi } from "./api";

/**
 * O que o painel guarda no navegador.
 *
 * É aqui que mora a diferença para o app antigo: uma tela já vista abre na
 * hora, com o que está guardado, e se atualiza por trás. Quem diz que algo
 * mudou é o servidor (`tempo-real.ts`), não um relógio — por isso o dado pode
 * ficar "fresco" por um bom tempo sem ficar velho.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // O aviso em tempo real cobre o que muda por aqui. O minuto cobre o que
      // muda por fora — alguém editando a planilha ou a agenda do Google.
      staleTime: 60_000,
      gcTime: 30 * 60_000,
      // Sessão vencida ou clínica trocada não se resolve tentando de novo.
      retry: (tentativas, erro) => !(erro instanceof ErroApi && erro.status >= 400 && erro.status < 500) && tentativas < 2,
      refetchOnWindowFocus: true,
    },
  },
});

export const chaves = {
  eu: ["eu"] as const,
  conversas: ["conversas"] as const,
  conversa: (telefone: string) => ["conversa", telefone] as const,
  pacientes: ["pacientes"] as const,
  agenda: ["agenda"] as const,
  funil: ["funil"] as const,
  relatorio: ["relatorio"] as const,
  modelos: ["modelos"] as const,
  empresa: ["empresa"] as const,
  conexao: ["conexao"] as const,
  equipe: ["equipe"] as const,
  configuracoes: ["configuracoes"] as const,
  sistema: ["sistema"] as const,
};

export const euQuery = () =>
  queryOptions({ queryKey: chaves.eu, queryFn: () => api<RespostaEu>("/eu"), staleTime: 5 * 60_000 });

export const conversasQuery = () =>
  queryOptions({ queryKey: chaves.conversas, queryFn: () => api<ListaConversas>("/conversas") });

export const conversaQuery = (telefone: string) =>
  queryOptions({ queryKey: chaves.conversa(telefone), queryFn: () => api<RespostaConversa>(`/conversas/${telefone}`) });

export const pacientesQuery = () => queryOptions({ queryKey: chaves.pacientes, queryFn: () => api<RespostaPacientes>("/pacientes") });
export const agendaQuery = () => queryOptions({ queryKey: chaves.agenda, queryFn: () => api<RespostaAgenda>("/agenda") });
export const funilQuery = () => queryOptions({ queryKey: chaves.funil, queryFn: () => api<RespostaFunil>("/funil") });
export const relatorioQuery = () => queryOptions({ queryKey: chaves.relatorio, queryFn: () => api<RespostaRelatorio>("/relatorio") });
export const modelosQuery = () => queryOptions({ queryKey: chaves.modelos, queryFn: () => api<RespostaModelos>("/modelos") });
export const conexaoQuery = () => queryOptions({ queryKey: chaves.conexao, queryFn: () => api<RespostaConexao>("/conexao") });
export const equipeQuery = () => queryOptions({ queryKey: chaves.equipe, queryFn: () => api<RespostaEquipe>("/equipe") });
export const configuracoesQuery = () =>
  queryOptions({ queryKey: chaves.configuracoes, queryFn: () => api<RespostaConfiguracoes>("/configuracoes") });
export const sistemaQuery = () => queryOptions({ queryKey: chaves.sistema, queryFn: () => api<RespostaSistema>("/sistema") });

/**
 * A Empresa é um FORMULÁRIO: ele nasce do que veio e depois é da pessoa.
 * Trocar o que está embaixo dele por trás, no meio da edição, apagaria o que
 * ela digitou — então esta consulta não se atualiza sozinha.
 */
export const empresaQuery = () =>
  queryOptions({ queryKey: chaves.empresa, queryFn: () => api<RespostaEmpresa>("/empresa"), staleTime: Infinity, refetchOnWindowFocus: false });
